import type { AppLanguage } from '@/i18n/translations';
import type { WizardAnswers } from '@/context/WizardSessionContext';
import { formatParcelCadastralLine } from '@/lib/planning-report-i18n';
import {
  isCommitteeMigrashLabel,
  isParcelPickKey,
  isPlanNumberLabel,
  normalizeMigrash,
} from '@/lib/validation/parcel';

export type WizardReviewRow = {
  label: string;
  value: string;
  dir?: 'ltr';
};

/** Parse internal map-pick keys (`ייעוד@lat,lng` or `pick@lat,lng`). */
export function parseParcelPickKey(migrash: string): {
  designation?: string;
  lat?: number;
  lng?: number;
} {
  const n = normalizeMigrash(migrash);
  const pickOnly = /^pick@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/i.exec(n);
  if (pickOnly) {
    return {
      lat: Number.parseFloat(pickOnly[1]!),
      lng: Number.parseFloat(pickOnly[2]!),
    };
  }

  const at = n.indexOf('@');
  if (at <= 0) return {};

  const before = n.slice(0, at).trim();
  const after = n.slice(at + 1).trim();
  const coords = /^(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/;
  const afterMatch = coords.exec(after);
  if (afterMatch) {
    return {
      designation: before || undefined,
      lat: Number.parseFloat(afterMatch[1]!),
      lng: Number.parseFloat(afterMatch[2]!),
    };
  }
  const beforeMatch = coords.exec(before);
  if (beforeMatch) {
    return {
      designation: after || undefined,
      lat: Number.parseFloat(beforeMatch[1]!),
      lng: Number.parseFloat(beforeMatch[2]!),
    };
  }
  return {};
}

function formatMapCoordinates(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

export function buildWizardReviewParcelRows(
  answers: WizardAnswers,
  labels: {
    designation: string;
    planPrimary: string;
    migrash: string;
    cadastral: string;
    mapLocation: string;
    drawnArea?: string;
  },
  language: AppLanguage,
): WizardReviewRow[] {
  const rows: WizardReviewRow[] = [];
  const migrash = answers.migrash?.trim();
  const isDrawn = answers.selection_mode === 'polygon_drawn';
  const parsed = migrash && isParcelPickKey(migrash) ? parseParcelPickKey(migrash) : {};

  const designation =
    answers.designation?.trim() || parsed.designation?.trim() || undefined;
  if (designation) {
    rows.push({ label: labels.designation, value: designation });
  }

  if (isDrawn && answers.area_sqm_drawn != null && labels.drawnArea) {
    rows.push({
      label: labels.drawnArea,
      value: `~${answers.area_sqm_drawn.toLocaleString('he-IL')} מ"ר`,
    });
  }

  const plan =
    answers.plan_primary?.trim() ||
    (migrash && isPlanNumberLabel(migrash) ? migrash.trim() : undefined);
  if (plan) {
    rows.push({ label: labels.planPrimary, value: plan, dir: 'ltr' });
  }

  if (migrash && isCommitteeMigrashLabel(migrash)) {
    rows.push({ label: labels.migrash, value: migrash });
  }

  const gush = answers.gush != null ? Number.parseInt(String(answers.gush), 10) : NaN;
  const helka = answers.helka != null ? Number.parseInt(String(answers.helka), 10) : NaN;
  if (Number.isFinite(gush) && Number.isFinite(helka)) {
    rows.push({
      label: labels.cadastral,
      value: formatParcelCadastralLine(gush, helka, language),
      dir: 'ltr',
    });
  }

  const lat = answers.map_lat ?? parsed.lat;
  const lng = answers.map_lng ?? parsed.lng;
  if (lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
    rows.push({
      label: labels.mapLocation,
      value: formatMapCoordinates(lat, lng),
      dir: 'ltr',
    });
  }

  return rows;
}
